import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trophy, PartyPopper, Target, Clock, Mail, TrendingUp, Inbox } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const goalTypeConfig = {
  daily_emails: { icon: Mail, label: "Email Giornaliere" },
  response_time: { icon: Clock, label: "Tempo di Risposta" },
  reply_rate: { icon: TrendingUp, label: "Tasso di Risposta" },
  inbox_zero: { icon: Inbox, label: "Inbox Zero" }
};

/**
 * Hook to monitor goal achievements and show notifications
 * Can be used in any component to get real-time goal updates
 */
export const useGoalNotifications = (enabled = true) => {
  const [goals, setGoals] = useState([]);
  const [achievedGoals, setAchievedGoals] = useState([]);
  const previousGoalsRef = useRef([]);
  const hasShownInitialRef = useRef(false);

  const fetchGoals = useCallback(async () => {
    if (!enabled) return;
    
    try {
      const response = await axios.get(`${API}/api/analytics/goals`, { withCredentials: true });
      const newGoals = response.data.goals || [];
      setGoals(newGoals);
      
      // Check for newly achieved goals (skip on initial load)
      if (hasShownInitialRef.current && previousGoalsRef.current.length > 0) {
        newGoals.forEach((goal) => {
          const prevGoal = previousGoalsRef.current.find(g => g.goal_id === goal.goal_id);
          if (prevGoal && !prevGoal.is_achieved && goal.is_achieved) {
            showGoalAchievedToast(goal);
            setAchievedGoals(prev => [...prev, goal]);
          }
        });
      }
      
      hasShownInitialRef.current = true;
      previousGoalsRef.current = newGoals;
    } catch (error) {
      console.error("Error fetching goals:", error);
    }
  }, [enabled]);

  const showGoalAchievedToast = (goal) => {
    const config = goalTypeConfig[goal.goal_type] || { icon: Target, label: "Obiettivo" };
    const Icon = config.icon;

    toast.custom((t) => (
      <div 
        className={`${
          t.visible ? 'animate-enter' : 'animate-leave'
        } max-w-md w-full bg-white dark:bg-zinc-900 shadow-lg rounded-xl pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden`}
      >
        <div className="p-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="p-3 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 shadow-md">
                <Trophy className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="flex-1 pt-0.5">
              <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                <PartyPopper className="w-4 h-4 text-yellow-500" />
                Obiettivo Raggiunto!
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {goal.name}
              </p>
              <div className="mt-2 flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                <Icon className="w-3 h-3" />
                <span>{config.label}: {goal.target_value} {goal.target_unit}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-green-400 to-emerald-500" />
      </div>
    ), {
      duration: 6000,
      position: "top-right",
    });
  };

  useEffect(() => {
    fetchGoals();
    // Poll every 60 seconds
    const interval = setInterval(fetchGoals, 60000);
    return () => clearInterval(interval);
  }, [fetchGoals]);

  // Clear achieved goals after they've been shown
  const clearAchievedGoals = useCallback(() => {
    setAchievedGoals([]);
  }, []);

  return {
    goals,
    achievedGoals,
    clearAchievedGoals,
    refreshGoals: fetchGoals
  };
};

/**
 * Component to be placed at app root level for global goal notifications
 */
export const GoalNotificationProvider = ({ children }) => {
  // This hook will automatically show notifications when goals are achieved
  useGoalNotifications(true);
  return children;
};

export default useGoalNotifications;
